import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { FirmsController } from "../../../api/src/firms/firms.controller";
import type { UpdateSecurityAssetCategoryOverridesDto } from "../../../api/src/firms/firms.dto";
import type { UnpackResponse } from "../../../api/src/lib";
import type { PositionSwapsController } from "../../../api/src/position-swaps/position-swaps.controller";
import type { CreatePositionSwapRequest } from "../../../api/src/position-swaps/position-swaps.types";
import type { SecurityDetailsController } from "../../../api/src/security-details/security-details.controller";
import type {
  MoveOutPreview,
  MoveOutRequest,
  SecurityDetails,
  SecurityMarketData,
  SecurityTransactions,
} from "../../../api/src/security-details/security-details.types";
import type { SecuritiesController } from "../../../api/src/securities/securities.controller";
import { deserializeTransaction } from "../components/TransactionsTable";
import { useAuthenticatedMutationNew, useAuthenticatedQuery } from "../lib/api";
import { POSITION_SWAP_LISTS_KEY } from "../rebalances/positionSwapsLib";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";

const dateOrNull = (value: string | null | undefined) =>
  value ? new Date(value) : null;

export function useQuerySecurityDetails(symbol: string) {
  return useAuthenticatedQuery<
    UnpackResponse<SecurityDetailsController["getDetails"]>,
    SecurityDetails
  >({
    queryKey: ["firm", "securities", symbol, "details"],
    urlBuilderFn: () =>
      `/firm/securities/${encodeURIComponent(symbol)}/details`,
    queryFn: async ({ data }) => ({
      ...data,
      holdingsSummary: {
        ...data.holdingsSummary,
        asOfDate: dateOrNull(data.holdingsSummary.asOfDate),
      },
      holdings: data.holdings.map((holding) => ({
        ...holding,
        firstOpenDate: dateOrNull(holding.firstOpenDate),
      })),
    }),
  });
}

type SerializedPoint = { date: string; price: number };
const deserializePoint = (point: SerializedPoint) => ({
  ...point,
  date: new Date(point.date),
});

/**
 * The FastTrack data of the security page (APP-1487): one part for each
 * FastTrack call, its data or a failed mark, with the API's chart periods. The
 * page shows its other sections while this loads or fails.
 */
export function useQuerySecurityMarketData(symbol: string) {
  return useAuthenticatedQuery<
    UnpackResponse<SecurityDetailsController["getMarketData"]>,
    SecurityMarketData
  >({
    queryKey: ["firm", "securities", symbol, "market-data"],
    urlBuilderFn: () =>
      `/firm/securities/${encodeURIComponent(symbol)}/market-data`,
    queryFn: async ({ data }) => {
      const periods = data.periods?.map((period) => ({
        ...period,
        firstDate: new Date(period.firstDate),
        points: period.points.map(deserializePoint),
      }));
      if (data.variant === "bond") {
        return { ...data, periods: periods ?? null };
      }
      return {
        ...data,
        periods:
          data.periods?.map((period, i) => ({
            ...periods![i],
            risk: period.risk,
            drawdown: period.drawdown && {
              ...period.drawdown,
              peakDate: new Date(period.drawdown.peakDate),
              valleyDate: new Date(period.drawdown.valleyDate),
              recoveryDate: dateOrNull(period.drawdown.recoveryDate),
            },
          })) ?? null,
        benchmarkPoints: data.benchmarkPoints?.map(deserializePoint) ?? null,
      };
    },
  });
}

/**
 * The firm's transactions in `symbol` from `startDate` to `endDate`, both
 * `YYYY-MM-DD` and both included, with their totals: the Activity tab. Asks
 * nothing while `enabled` is false.
 */
export function useQuerySecurityTransactions(
  symbol: string,
  startDate: string,
  endDate: string,
  enabled: boolean,
) {
  return useAuthenticatedQuery<
    UnpackResponse<SecurityDetailsController["getTransactions"]>,
    SecurityTransactions
  >({
    queryKey: [
      "firm",
      "securities",
      symbol,
      "transactions",
      startDate,
      endDate,
    ],
    urlBuilderFn: () =>
      `/firm/securities/${encodeURIComponent(symbol)}/transactions?${new URLSearchParams({ startDate, endDate })}`,
    queryFn: async ({ data }) => ({
      ...data,
      transactions: data.transactions.map(deserializeTransaction),
    }),
    queryOptions: { enabled },
  });
}

export function usePreviewMoveOut(symbol: string) {
  return useAuthenticatedMutationNew<
    UnpackResponse<SecurityDetailsController["previewMoveOut"]>,
    MoveOutPreview,
    MoveOutRequest
  >({
    mutationKey: ["POST", "firm", "securities", symbol, "move-out", "preview"],
    urlBuilderFn: () =>
      `/firm/securities/${encodeURIComponent(symbol)}/move-out/preview`,
    mutationFn: ({ data }) => data as MoveOutPreview,
  });
}

/** Saves a move-out as a draft position swap. Answers the swap's id and size. */
export function useCreatePositionSwap() {
  const queryClient = useQueryClient();
  return useAuthenticatedMutationNew<
    UnpackResponse<PositionSwapsController["create"]>,
    { id: number; households: number },
    CreatePositionSwapRequest
  >({
    mutationKey: ["POST", "firm", "position-swaps"],
    urlBuilderFn: () => "/firm/position-swaps",
    mutationFn: ({ data }) => ({ id: data.id, households: data.households }),
    mutationOptions: {
      // The new draft joins the open swaps of the security.
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: POSITION_SWAP_LISTS_KEY }),
    },
  });
}

/**
 * The mapping write: the firm's security category mapping route, which the
 * Security Master screen also writes through. It is the first element of the
 * Security Master's query keys, so invalidating it refreshes that screen.
 */
const SECURITY_ASSET_CATEGORIES_PATH = "/v1/firm/securities/asset-categories";

/** The FastTrack categories a security can be mapped to, sorted by name. */
export function useQueryAssetCategories() {
  const { rows } = useAuthenticatedFetchAllPages<
    UnpackResponse<SecuritiesController["getAssetCategories"]>["data"][number]
  >("/v1/securities/asset-categories");
  return useMemo(
    () => [...new Set(rows.map((row) => row.fasttrackCategory))].sort(),
    [rows],
  );
}

/**
 * Sets the firm's category of `security` through the mapping write. The route
 * overwrites every column of the firm's row, so the security's current CUSIP,
 * description, security type (from which the route derives `is_bond`), ADV
 * category and 13-F are sent back with the new category.
 */
export function useUpdateSecurityCategory(
  security: SecurityDetails["security"],
) {
  const queryClient = useQueryClient();
  return useAuthenticatedMutationNew<
    UnpackResponse<FirmsController["updateSecurityAssetCategoryOverrides"]>,
    void,
    UpdateSecurityAssetCategoryOverridesDto
  >({
    mutationKey: ["PUT", "firm", "securities", "asset-categories"],
    urlBuilderFn: () => SECURITY_ASSET_CATEGORIES_PATH,
    mutationFn: () => undefined,
    mutationOptions: {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["firm", "securities", security.symbol, "details"],
          }),
          queryClient.invalidateQueries({
            queryKey: [SECURITY_ASSET_CATEGORIES_PATH],
          }),
        ]),
    },
  });
}

/**
 * The body of the mapping write that maps `security` to `category`. The ADV
 * category and 13F flag go along only when the firm already overrides them:
 * `security` carries the resolved market values, and writing those would pin
 * them as firm overrides (as `SecurityMaster.overrideForAcceptedSuggestion`).
 */
export const categoryWrite = (
  security: SecurityDetails["security"],
  category: string,
): UpdateSecurityAssetCategoryOverridesDto => ({
  [security.symbol]: {
    category,
    cusip: security.cusip || undefined,
    description: security.description || undefined,
    securityType: security.securityType || undefined,
    advCategory: security.isAdvCategoryOverride
      ? security.advCategory || undefined
      : undefined,
    is13f: security.isIs13fOverride ? (security.is13f ?? undefined) : undefined,
    mode: "INSERT",
  },
});

/** How the page names a move-out reason. */
export const MOVE_OUT_REASON_LABELS: Record<
  MoveOutPreview["accounts"][number]["reason"],
  string
> = {
  tax_deferred: "Tax-deferred: sells in full",
  losses_only: "Sells its lots at a loss",
  gain_limit: "Sells within the household's gain limit",
  all: "Sells in full",
  tax_deferred_kept: "Tax-deferred: kept",
  taxable_kept: "Taxable: kept",
  no_loss_lots: "No lots at a loss",
  gain_limit_reached: "Household gain limit reached",
  recent_purchase: "Bought in the last 30 days (wash sale)",
  restricted: "Restricted: hold only, excluded, or no loss to sell",
  no_cost_basis: "Cost basis unknown",
  no_household: "Not in a household: can't be swapped",
  sell_no_tax: "Sell if no tax impact: sells its lots at a loss",
};
